import React, { useState, useEffect, useRef } from 'react';
import { Form, Row, Col, Card, Button, Table, Badge, Offcanvas, Spinner, ProgressBar } from 'react-bootstrap';
import {
    FaSave, FaFileInvoice, FaCheckCircle, FaTimes, FaInfoCircle,
    FaArrowLeft, FaArrowRight, FaDownload, FaEye, FaPrint
} from 'react-icons/fa';
import axios from 'axios';
import Swal from 'sweetalert2';
import './CreditNote.css'; // Custom CSS for wizard
import CreditNotePreview from './CreditNotePreview';

const CreditNoteCreateOffcanvas = ({
    show,
    handleClose,
    invoice,
    type,
    onCreditNoteCreated
}) => {
    // Wizard state
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [companyDetails, setCompanyDetails] = useState(null);
    const [createdCreditNote, setCreatedCreditNote] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);

    // Credit note form state - Initialize with type from props
    const [creditNote, setCreditNote] = useState({
        invoice_id: invoice?.id || null,
        invoice_type: type || 'tax', // Use the type from props
        date: new Date().toISOString().split('T')[0], // Today's date
        reason: '',
        credit_type: 'full',
        items: [],
        total_basic_amount: 0,
        total_vat_amount: 0,
        total_amount: 0,
        remarks: '',
        client_attention: '',
        client_phone: '',
        client_email: ''
    });

    // Steps configuration
    const steps = [
        { number: 1, title: 'Basic Details', description: '' },
        { number: 2, title: 'Credit Items', description: '' },
        { number: 3, title: 'Review & Preview', description: '' },
        { number: 4, title: 'Complete', description: '' }
    ];

    // Add useRef to track if data fetching has started
    const fetchStartedRef = useRef(false);

    // Fetch invoice and company details
    useEffect(() => {
        if (show && invoice && !fetchStartedRef.current) {
            console.log('🚀 Starting fetchInitialData...', {
                show,
                invoiceId: invoice.id,
                invoiceRef: invoice.ref_no,
                type
            });
            fetchStartedRef.current = true;
            fetchInitialData();
        }

        // Reset when component closes
        return () => {
            if (!show) {
                fetchStartedRef.current = false;
                setDataLoaded(false);
            }
        };
    }, [show, invoice, type]);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            // 1. Fetch company details from settings API
            console.log('📋 Fetching company details...');
            const companyResponse = await axios.get(`${backendUrl}/api/settings`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true // Custom header to skip global loader if implemented
            });

            if (companyResponse.data.setting) {
                setCompanyDetails(companyResponse.data.setting);
                console.log('✅ Company details loaded:', companyResponse.data.setting.name);
            }

            // 2. Fetch invoice details
            console.log('📋 Fetching invoice details... Type:', type, 'Invoice ID:', invoice.id);

            const endpoint = type === 'tax'
                ? `${backendUrl}/api/taxinvoice/${invoice.id}`
                : `${backendUrl}/api/proformainvoice/${invoice.id}`;

            console.log('🌐 API Endpoint:', endpoint);

            const invoiceResponse = await axios.get(endpoint, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true // Custom header to skip global loader if implemented
            });

            console.log('📦 Invoice response:', invoiceResponse.data);

            const invoiceData = invoiceResponse.data.taxinvoice ||
                invoiceResponse.data.proformainvoice ||
                invoiceResponse.data.data;

            console.log('📄 Processed invoice data:', {
                id: invoiceData?.id,
                ref_no: invoiceData?.ref_no,
                hasItems: !!invoiceData?.product_items,
                client: invoiceData?.quotation?.project?.client?.name,
                amount: invoiceData?.basic_amount || invoiceData?.amount
            });

            // Initialize credit note items
            if (invoiceData) {
                const client = invoiceData.quotation?.project?.client ||
                    invoiceData.proforma_invoice?.quotation?.project?.client;

                console.log('👤 Client data:', client);

                // Get items from different possible locations
                let items = [];
                if (invoiceData.product_items && invoiceData.product_items.length > 0) {
                    items = invoiceData.product_items;
                } else if (invoiceData.proforma_invoice?.quotation?.product_item_details) {
                    items = invoiceData.proforma_invoice.quotation.product_item_details;
                } else if (invoiceData.quotation?.product_item_details) {
                    items = invoiceData.quotation.product_item_details;
                }

                console.log('🛒 Found items:', items.length);

                // In fetchInitialData function, inside the map for items:
                const creditNoteItems = items.map(item => {
                    // Handle null product case
                    const hasProduct = item.product_id || item.product;
                    const description = hasProduct
                        ? (item.product?.name || item.description || 'Service/Product')
                        : (item.description || 'Service/Product');

                    const unitPrice = parseFloat(item.unit_price || item.price || 0);
                    const quantity = parseFloat(item.quantity) || 1; // Default to 1 if null/undefined
                    const subtotal = unitPrice * quantity;
                    const vatAmount = subtotal * 0.05;

                    return {
                        id: item.id,
                        product_id: item.product_id || null,
                        description: description,
                        unit_price: unitPrice,
                        quantity: quantity,
                        subtotal: subtotal,
                        vat_amount: vatAmount,
                        total_amount: subtotal + vatAmount,
                        credit_quantity: quantity, // Default full credit
                        credit_subtotal: subtotal,
                        credit_vat: vatAmount,
                        credit_amount: subtotal + vatAmount,
                        hasProduct: !!hasProduct // Flag to indicate if product exists
                    };
                });

                const total_basic_amount = parseFloat(invoiceData.basic_amount || invoiceData.amount || 0);
                const total_vat_amount = parseFloat(invoiceData.vat_amount || 0);
                const total_amount = parseFloat(invoiceData.total_with_vat ||
                    (total_basic_amount + total_vat_amount));

                console.log('💰 Totals calculated:', {
                    total_basic_amount,
                    total_vat_amount,
                    total_amount
                });

                // Update credit note state
                setCreditNote(prev => {
                    const updatedState = {
                        ...prev,
                        invoice_id: invoice.id,
                        invoice_type: type, // Make sure type is correct
                        items: creditNoteItems,
                        total_basic_amount,
                        total_vat_amount,
                        total_amount,
                        client_attention: client?.contact_person || client?.name || '',
                        client_phone: client?.phone || '',
                        client_email: client?.email || ''
                    };

                    console.log('🔄 Setting credit note state:', updatedState);
                    return updatedState;
                });

                setDataLoaded(true);
                console.log('✅ Data loaded successfully');
            } else {
                console.error('❌ No invoice data found');
                Swal.fire('Error', 'No invoice data found', 'error');
            }

        } catch (error) {
            console.error('❌ Error fetching initial data:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            Swal.fire('Error', `Failed to load data: ${error.message}`, 'error');
        } finally {
            setLoading(false);
            fetchStartedRef.current = false;
        }
    };

    // Debug useEffect to log creditNote changes
    useEffect(() => {
        console.log('📊 CreditNote state updated:', {
            invoice_id: creditNote.invoice_id,
            invoice_type: creditNote.invoice_type,
            items_count: creditNote.items.length,
            total_amount: creditNote.total_amount,
            dataLoaded
        });
    }, [creditNote, dataLoaded]);

    // Wizard navigation
    const nextStep = () => {
        if (currentStep < steps.length) {
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    // Handle form changes
    const handleInputChange = (field, value) => {
        setCreditNote(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleQuantityChange = (index, value) => {
        const items = [...creditNote.items];
        const item = items[index];

        const maxQuantity = parseFloat(item.quantity);
        const creditQuantity = Math.min(Math.max(0, parseFloat(value) || 0), maxQuantity);

        const creditSubtotal = creditQuantity * item.unit_price;
        const creditVat = creditSubtotal * 0.05;
        const creditTotal = creditSubtotal + creditVat;

        items[index] = {
            ...item,
            credit_quantity: creditQuantity,
            credit_subtotal: creditSubtotal,
            credit_vat: creditVat,
            credit_amount: creditTotal
        };

        updateTotals(items);
    };

    const updateTotals = (items) => {
        const total_basic_amount = items.reduce((sum, item) => sum + (item.credit_subtotal || 0), 0);
        const total_vat_amount = items.reduce((sum, item) => sum + (item.credit_vat || 0), 0);
        const total_amount = total_basic_amount + total_vat_amount;

        // Get original invoice total
        let originalTotal = 0;
        if (invoice) {
            originalTotal = parseFloat(invoice.total_with_vat ||
                (parseFloat(invoice.basic_amount || invoice.amount || 0) +
                    parseFloat(invoice.vat_amount || 0)));
        }

        const credit_type = Math.abs(total_amount - originalTotal) < 0.01 ? 'full' : 'partial';

        setCreditNote(prev => ({
            ...prev,
            items,
            total_basic_amount,
            total_vat_amount,
            total_amount,
            credit_type
        }));
    };

    // Create credit note
    const handleSubmit = async () => {
        try {
            setSaving(true);

            // Validate
            if (!creditNote.reason.trim()) {
                Swal.fire('Error', 'Please enter a reason for credit note', 'error');
                return;
            }

            if (creditNote.total_amount <= 0) {
                Swal.fire('Error', 'Credit note amount must be greater than 0', 'error');
                return;
            }

            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const clientId = invoice.quotation?.project?.client?.id ||
                invoice.proforma_invoice?.quotation?.project?.client?.id;

            console.log('📤 Submitting credit note:', {
                creditNote,
                clientId,
                invoice_ref: invoice.ref_no
            });

            const response = await axios.post(`${backendUrl}/api/credit-notes`, {
                ...creditNote,
                client_id: clientId,
                invoice_ref: invoice.ref_no,
                company_details: companyDetails
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                }
            });

            if (response.data.status === 'success') {
                console.log('✅ Credit note created:', response.data.credit_note);
                setCreatedCreditNote(response.data.credit_note);
                setCurrentStep(4); // Go to completion step

                if (onCreditNoteCreated) {
                    onCreditNoteCreated();
                }
            }
        } catch (error) {
            console.error('❌ Error creating credit note:', error);
            console.error('Error response:', error.response?.data);
            Swal.fire('Error', error.response?.data?.message || 'Failed to create credit note', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Download PDF
    const handleDownloadPDF = async (creditNoteId, refNo) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(
                `${backendUrl}/api/credit-notes/${creditNoteId}/download`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                    responseType: 'blob'
                }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = `credit-note-${refNo}.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Error downloading PDF:', error);
            Swal.fire('Error', 'Failed to download PDF', 'error');
        }
    };

    // Close offcanvas
    const handleCloseOffcanvas = () => {
        setCurrentStep(1);
        setCreditNote({
            invoice_id: null,
            invoice_type: type || 'tax', // Use current type
            date: new Date().toISOString().split('T')[0],
            reason: '',
            credit_type: 'full',
            items: [],
            total_basic_amount: 0,
            total_vat_amount: 0,
            total_amount: 0,
            remarks: '',
            client_attention: '',
            client_phone: '',
            client_email: ''
        });
        setCreatedCreditNote(null);
        setDataLoaded(false);
        fetchStartedRef.current = false;
        handleClose();
    };


    const Step2CreditItems = () => {
        // First, check what's in creditNote.items
        console.log('Step2CreditItems - creditNote.items:', {
            items: creditNote.items,
            length: creditNote.items.length,
            dataLoaded: dataLoaded,
            hasNullProducts: creditNote.items.some(item => !item.product_id && !item.description)
        });

        // Show loading state if data is not loaded yet
        if (!dataLoaded) {
            return (
                <Card>
                    <Card.Header className="bg-light">
                        <h6 className="mb-0">Step 2: Select Items to Credit</h6>
                    </Card.Header>
                    <Card.Body className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3">Loading invoice items...</p>
                    </Card.Body>
                </Card>
            );
        }

        // Check if items array is empty
        // If empty, create a default item with null description
        if (creditNote.items.length === 0) {
            // Automatically create a default item
            const defaultItem = {
                id: Date.now(),
                product_id: null,
                description: null, // Keep it null as requested
                unit_price: parseFloat(invoice?.basic_amount || invoice?.amount || 0),
                quantity: 1, // Default quantity as requested
                subtotal: parseFloat(invoice?.basic_amount || invoice?.amount || 0),
                vat_amount: parseFloat(invoice?.vat_amount || 0),
                total_amount: parseFloat(invoice?.total_with_vat ||
                    (parseFloat(invoice?.basic_amount || invoice?.amount || 0) +
                        parseFloat(invoice?.vat_amount || 0))),
                credit_quantity: 1, // Default full credit
                credit_subtotal: parseFloat(invoice?.basic_amount || invoice?.amount || 0),
                credit_vat: parseFloat(invoice?.vat_amount || 0),
                credit_amount: parseFloat(invoice?.total_with_vat ||
                    (parseFloat(invoice?.basic_amount || invoice?.amount || 0) +
                        parseFloat(invoice?.vat_amount || 0)))
            };

            // Update state with the default item
            setCreditNote(prev => {
                const updatedItems = [defaultItem];
                const total_basic_amount = defaultItem.credit_subtotal;
                const total_vat_amount = defaultItem.credit_vat;
                const total_amount = defaultItem.credit_amount;

                return {
                    ...prev,
                    items: updatedItems,
                    total_basic_amount,
                    total_vat_amount,
                    total_amount,
                    credit_type: 'full'
                };
            });

            // Show loading while state updates
            return (
                <Card>
                    <Card.Header className="bg-light">
                        <h6 className="mb-0">Step 2: Select Items to Credit</h6>
                    </Card.Header>
                    <Card.Body className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3">Creating default item for credit...</p>
                    </Card.Body>
                </Card>
            );
        }

        // Check if any items have null product
        const hasNullProducts = creditNote.items.some(item => !item.product_id && !item.description);

        return (
            <Card>
                <Card.Header className="bg-light">
                    <h6 className="mb-0">Step 2: Select Items to Credit</h6>
                    <small className="text-muted">
                        {creditNote.items.length} item(s) found |
                        Invoice Type: {creditNote.invoice_type}
                        {hasNullProducts && " | Some items have no product details"}
                    </small>
                </Card.Header>
                <Card.Body>
                    <div className="table-responsive">
                        <Table bordered hover className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th width="5%">#</th>
                                    <th width="40%">Description</th>
                                    <th width="10%" className="text-center">Unit Price</th>
                                    <th width="10%" className="text-center">Credit Qty</th>
                                    <th width="10%" className="text-end">Subtotal</th>
                                    <th width="10%" className="text-end">VAT (5%)</th>
                                    <th width="15%" className="text-end">Total Credit</th>
                                </tr>
                            </thead>
                            <tbody>
                                {creditNote.items.map((item, index) => {
                                    // Handle null product - use default values as requested
                                    const description = item.description; // Keep it null if it's null
                                    const quantity = item.quantity || 1; // Default to 1 if null
                                    const unitPrice = parseFloat(item.unit_price || item.price || 0);
                                    const subtotal = unitPrice * quantity;

                                    return (
                                        <tr key={item.id || index}>
                                            <td>{index + 1}</td>
                                            <td>
                                                <small>
                                                    {description === null ? (
                                                        <span className="text-muted">
                                                            <FaInfoCircle className="me-1" />
                                                            No description
                                                        </span>
                                                    ) : description || (
                                                        <span className="text-warning">
                                                            <FaInfoCircle className="me-1" />
                                                            No description available
                                                        </span>
                                                    )}
                                                </small>
                                            </td>
                                            <td className="text-center">
                                                {unitPrice.toFixed(2)}
                                            </td>

                                            <td className="text-center">
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    max={quantity}
                                                    step="1"
                                                    value={item.credit_quantity || 0}
                                                    onChange={(e) => handleQuantityChange(index, e.target.value)}
                                                    className="text-center"
                                                    size="sm"
                                                />
                                            </td>
                                            <td className="text-end">
                                                <span className={item.credit_subtotal > 0 ? 'text-danger' : ''}>
                                                    {(item.credit_subtotal || 0).toFixed(2)}
                                                </span>
                                            </td>
                                            <td className="text-end">
                                                <span className="text-danger">
                                                    {(item.credit_vat || 0).toFixed(2)}
                                                </span>
                                            </td>
                                            <td className="text-end">
                                                <strong className="text-danger">
                                                    {(item.credit_amount || 0).toFixed(2)}
                                                </strong>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>


                    {/* Summary Card */}
                    <Card className="mt-3 border-primary">
                        <Card.Body className="p-3">
                            <Row>
                                <Col md={8}>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>Subtotal:</span>
                                        <span className="fw-bold">
                                            {creditNote.total_basic_amount.toFixed(2)} AED
                                        </span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>VAT (5%):</span>
                                        <span className="fw-bold">
                                            {creditNote.total_vat_amount.toFixed(2)} AED
                                        </span>
                                    </div>
                                    <hr />
                                    <div className="d-flex justify-content-between">
                                        <span className="h5">Total Credit Amount:</span>
                                        <span className="h4 text-danger">
                                            -{creditNote.total_amount.toFixed(2)} AED
                                        </span>
                                    </div>
                                </Col>
                                <Col md={4} className="text-center">
                                    <div className="border p-3 rounded bg-light">
                                        <h6>Credit Type</h6>
                                        <Badge bg={creditNote.credit_type === 'full' ? 'success' : 'warning'} className="fs-6 p-2">
                                            {creditNote.credit_type.toUpperCase()} CREDIT
                                        </Badge>
                                        <div className="mt-2 small text-muted">
                                            {creditNote.credit_type === 'full'
                                                ? 'Entire invoice amount will be credited'
                                                : 'Partial amount will be credited'}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Add item button - Only show if you want manual addition */}
                    {/* Removed as per requirement */}
                </Card.Body>
            </Card>
        );
    };

    // Step 3: Preview Component (matches CreditNotePreview design)
    const Step3Preview = () => {
        const [companyDetails, setCompanyDetails] = useState(null);
        const [bankAccounts, setBankAccounts] = useState([]);

        useEffect(() => {
            fetchCompanyDetails();
            fetchBankAccounts();
        }, []);

        const fetchCompanyDetails = async () => {
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const userInfo = JSON.parse(localStorage.getItem("user-info"));
                const origin = localStorage.getItem('user-origin');
                const token = userInfo?.token;

                const response = await axios.get(`${backendUrl}/api/settings`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                    skipLoader: true,
                });

                setCompanyDetails(response.data.setting);
            } catch (error) {
                console.error("Error fetching company details:", error);
            }
        };

        const fetchBankAccounts = async () => {
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const userInfo = JSON.parse(localStorage.getItem("user-info"));
                const origin = localStorage.getItem('user-origin');
                const token = userInfo?.token;

                const response = await axios.get(`${backendUrl}/api/bank-accounts?type=bank account`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Tenant-Origin": origin,
                    },
                    skipLoader: true,
                });

                const firstTwoAccounts = (response.data.account || []).slice(0, 2);
                setBankAccounts(firstTwoAccounts);
            } catch (error) {
                console.error("Error fetching bank accounts:", error);
                setBankAccounts([]);
            }
        };

        const formatAmount = (amount) => {
            return parseFloat(amount || 0).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        };

        const numberToWords = (number) => {
            const words = [
                'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
                'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
                'seventeen', 'eighteen', 'nineteen'
            ];

            const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

            if (number < 20) return words[number];
            if (number < 100) {
                return tens[Math.floor(number / 10)] + (number % 10 ? '-' + words[number % 10] : '');
            }
            if (number < 1000) {
                return words[Math.floor(number / 100)] + ' hundred' + (number % 100 ? ' and ' + numberToWords(number % 100) : '');
            }
            return number.toString();
        };

        const generateAmountInWords = (amount, currency = 'AED') => {
            const integerPart = Math.floor(amount);
            const decimalPart = Math.round((amount - integerPart) * 100);

            let words = numberToWords(integerPart);
            words = words.charAt(0).toUpperCase() + words.slice(1);

            if (decimalPart > 0) {
                words += ' and ' + numberToWords(decimalPart) + ' cents';
            }

            const currencyLabel = currency === 'AED' ? 'UAE' : 'USD';
            return `${currencyLabel} ${words}${decimalPart > 0 ? ` and ${decimalPart}/100` : ''} only.`;
        };

        if (!dataLoaded) {
            return (
                <Card>
                    <Card.Header className="bg-light">
                        <h6 className="mb-0">Step 3: Credit Note Preview</h6>
                    </Card.Header>
                    <Card.Body className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3">Loading preview data...</p>
                    </Card.Body>
                </Card>
            );
        }

        const clientCurrency = 'AED';
        const vatRate = 0.05;
        const matchedBanks = bankAccounts.filter(bank =>
            bank.currency?.currency?.toUpperCase() === clientCurrency?.toUpperCase()
        );

        return (
            <Card>
                <Card.Header className="bg-light">
                    <h6 className="mb-0">Step 3: Credit Note Preview</h6>
                    <small className="text-muted">Final preview before creation</small>
                </Card.Header>
                <Card.Body>
                    {/* Same design as CreditNotePreview component */}
                    <div className="preview-container" style={{ fontFamily: 'Arial, sans-serif', fontSize: '12px' }}>
                        {/* Header Section */}
                        <div className="header" style={{ display: 'flex', justifyContent: 'space-between', position: 'relative', marginBottom: '20px' }}>
                            <div className="header-left">
                                <h2 className="company-name" style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '6px' }}>
                                    {companyDetails?.company_name || 'Systech Technologies'}
                                </h2>
                                <p className="company-details" style={{ fontSize: '12px', lineHeight: '1.5', margin: 0 }}>
                                    {companyDetails?.company_address ? (
                                        <div dangerouslySetInnerHTML={{ __html: companyDetails.company_address }} />
                                    ) : (
                                        <>
                                            Dar Al Nahda Building, Office 307<br />
                                            P.O Box 91607
                                        </>
                                    )}
                                    {companyDetails?.phone && <>{companyDetails.phone}<br /></>}
                                    {companyDetails?.email && <>{companyDetails.email}<br /></>}
                                </p>
                            </div>
                            <div className="header-right" style={{ position: 'absolute', right: 0, top: 0, textAlign: 'right' }}>
                                {companyDetails?.logo && (
                                    <img
                                        src={`${process.env.REACT_APP_BACKEND_URL}/storage/${companyDetails.logo}`}
                                        style={{ maxWidth: '180px', maxHeight: '80px', marginBottom: '10px' }}
                                        alt="Company Logo"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                )}
                                {/* <div className="document-title" style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545', marginTop: '12px' }}>
                                CREDIT NOTE
                            </div> */}
                            </div>
                        </div>

                        <div className="divider" style={{ borderTop: '1px solid #ddd', margin: '10px 0 20px 0' }}></div>

                        {/* Credit Note Details */}
                        <div className="details-container" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', width: '100%' }}>
                            <div className="left-column" style={{ float: 'left', width: '48%' }}>
                                <div className="document-info">
                                    <div className="detail-row" style={{ marginBottom: '8px' }}>
                                        <strong className="detail-label" style={{ display: 'inline-block', width: '120px', fontWeight: 'bold' }}>Credit Note No:</strong>
                                        <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                            CN-{new Date().getFullYear()}-XXXXX
                                        </span>
                                    </div>
                                    <div className="detail-row" style={{ marginBottom: '8px' }}>
                                        <strong className="detail-label" style={{ display: 'inline-block', width: '120px', fontWeight: 'bold' }}>Date:</strong>
                                        <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                            {new Date(creditNote.date).toLocaleDateString('en-GB')}
                                        </span>
                                    </div>
                                    <div className="detail-row" style={{ marginBottom: '8px' }}>
                                        <strong className="detail-label" style={{ display: 'inline-block', width: '120px', fontWeight: 'bold' }}>Original Invoice:</strong>
                                        <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                            {creditNote.invoice_type === 'tax' ? 'Tax Invoice' : 'Proforma Invoice'}: {invoice?.ref_no || ''}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="right-column" style={{ float: 'right', width: '48%' }}>
                                <div className="customer-info">
                                    <div className="detail-row" style={{ marginBottom: '8px' }}>
                                        <strong className="detail-label" style={{ display: 'inline-block', width: '120px', fontWeight: 'bold' }}>Customer:</strong>
                                        <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                            {invoice?.quotation?.project?.client?.name || 'Client Name'}
                                        </span>
                                    </div>

                                    <div className="detail-row" style={{ marginBottom: '8px' }}>
                                        <strong className="detail-label" style={{ display: 'inline-block', width: '120px', fontWeight: 'bold' }}>Contact Phone:</strong>
                                        <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                            {creditNote.client_phone || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="detail-row" style={{ marginBottom: '8px' }}>
                                        <strong className="detail-label" style={{ display: 'inline-block', width: '120px', fontWeight: 'bold' }}>Contact Email:</strong>
                                        <span className="detail-value" style={{ display: 'inline-block', marginLeft: '8px' }}>
                                            {creditNote.client_email || 'N/A'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Items Table */}
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '11px' }}>
                            <thead>
                                <tr>
                                    <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Description</th>
                                    <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Original Qty</th>
                                    <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Credited Qty</th>
                                    <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Unit Price</th>
                                    <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Subtotal</th>
                                    <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>{vatRate * 100}% VAT</th>
                                    <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#003366', color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {creditNote.items && creditNote.items.length > 0 ? (
                                    creditNote.items
                                        .filter(item => item.credit_quantity > 0)
                                        .map((item, index) => {
                                            const creditSubtotal = parseFloat(item.credit_subtotal || 0);
                                            const creditVatAmount = parseFloat(item.credit_vat || 0);
                                            const creditTotal = creditSubtotal + creditVatAmount;

                                            return (
                                                <tr key={index}>
                                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                                                        {item.description || 'Service/Product'}
                                                    </td>
                                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                                        {item.quantity || 0}
                                                    </td>
                                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', color: '#dc3545', fontWeight: 'bold' }}>
                                                        -{item.credit_quantity || 0}
                                                    </td>
                                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>
                                                        {formatAmount(item.unit_price)}
                                                    </td>
                                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', color: '#dc3545' }}>
                                                        -{formatAmount(creditSubtotal)}
                                                    </td>
                                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', color: '#dc3545' }}>
                                                        -{formatAmount(creditVatAmount)}
                                                    </td>
                                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', color: '#dc3545', fontWeight: 'bold' }}>
                                                        -{formatAmount(creditTotal)}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                ) : (
                                    <tr>
                                        <td colSpan="7" style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', fontStyle: 'italic' }}>
                                            No credited items available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            <tfoot>
                                <tr style={{ backgroundColor: '#f8f9fa' }}>
                                    <td colSpan="4" style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold', textAlign: 'right' }}>
                                        Total Credit Amount:
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#dc3545' }}>
                                        -{formatAmount(creditNote.total_basic_amount)}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#dc3545' }}>
                                        -{formatAmount(creditNote.total_vat_amount)}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#dc3545' }}>
                                        -{formatAmount(creditNote.total_amount)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>

                        {/* Amount Summary */}
                        <div style={{ width: '250px', marginLeft: 'auto', fontSize: '11px', marginTop: '10px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', border: 'none' }}>
                                <tr>
                                    <td style={{ padding: '4px 0', fontWeight: 'bold', textAlign: 'left', width: '60%', border: 'none' }}>
                                        Subtotal:
                                    </td>
                                    <td style={{ padding: '4px 0', textAlign: 'right', color: '#dc3545', border: 'none' }}>
                                        -{formatAmount(creditNote.total_basic_amount)}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '4px 0', fontWeight: 'bold', textAlign: 'left', border: 'none' }}>
                                        VAT ({vatRate * 100}%):
                                    </td>
                                    <td style={{ padding: '4px 0', textAlign: 'right', color: '#dc3545', border: 'none' }}>
                                        -{formatAmount(creditNote.total_vat_amount)}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '6px 0', fontWeight: 'bold', textAlign: 'left', border: 'none' }}>Total Credit:</td>
                                    <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 'bold', color: '#dc3545', border: 'none' }}>
                                        -{formatAmount(creditNote.total_amount)}
                                    </td>
                                </tr>
                            </table>
                        </div>

                        {/* Amount in Words */}
                        <div style={{
                            marginTop: '20px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            padding: '10px 15px',
                            background: '#fafafa',
                            fontSize: '13px',
                            color: '#000'
                        }}>
                            <strong>Credit Amount (in Words):</strong> {generateAmountInWords(creditNote.total_amount, clientCurrency)}
                        </div>

                        {/* Bank Details */}
                        {matchedBanks.length > 0 && (
                            <div className="bank-details" style={{
                                marginTop: '20px',
                                border: '1px solid #ccc',
                                borderRadius: '6px',
                                padding: '12px',
                                background: '#fff'
                            }}>
                                <h4 style={{
                                    margin: '0 0 10px 0',
                                    fontSize: '13px',
                                    color: '#000',
                                    borderBottom: '1px solid #003366',
                                    paddingBottom: '5px'
                                }}>
                                    Bank Details
                                </h4>
                                {/* Bank details table */}
                                {matchedBanks.map((bank, index) => (
                                    <table key={index} width="100%" style={{ borderCollapse: 'collapse', fontSize: '12px', marginBottom: index < matchedBanks.length - 1 ? '15px' : '0' }}>
                                        <tr>
                                            <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>Account Name:</td>
                                            <td style={{ padding: '4px 0' }}>{bank.account_holder_name}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>Bank Name:</td>
                                            <td style={{ padding: '4px 0' }}>{bank.name}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>Account No:</td>
                                            <td style={{ padding: '4px 0' }}>{bank.account_number}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>IBAN:</td>
                                            <td style={{ padding: '4px 0' }}>{bank.iban}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ width: '150px', padding: '4px 0', fontWeight: 'bold' }}>SWIFT:</td>
                                            <td style={{ padding: '4px 0' }}>{bank.swift_code}</td>
                                        </tr>
                                    </table>
                                ))}
                            </div>
                        )}

                        {/* Remarks */}
                        {creditNote.reason && (
                            <div style={{
                                marginTop: '20px',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '12px',
                                background: '#f8f9fa'
                            }}>
                                <h4 style={{
                                    margin: '0 0 10px 0',
                                    fontSize: '13px',
                                    color: '#6c757d',
                                    borderBottom: '1px solid #6c757d',
                                    paddingBottom: '5px'
                                }}>
                                    Remarks
                                </h4>
                                <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
                                    {creditNote.reason}
                                </div>
                            </div>
                        )}


                    </div>
                </Card.Body>
            </Card>
        );
    };

    // Step 4: Completion Component
    const Step4Complete = () => {
        // State to show preview offcanvas
        const [showPreview, setShowPreview] = useState(false);

        return (
            <>
                <Card>
                    <Card.Header className="bg-light">
                        <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">
                                <FaCheckCircle className="me-2 text-success" />
                                Credit Note Created Successfully!
                            </h6>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleDownloadPDF(createdCreditNote.id, createdCreditNote.ref_no)}
                                >
                                    <FaDownload className="me-1" />
                                    Download PDF
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => setShowPreview(true)}
                                >
                                    <FaEye className="me-1" />
                                    View Details
                                </Button>
                            </div>
                        </div>
                    </Card.Header>
                    <Card.Body className="text-center py-5">
                        <div className="mb-4">
                            <FaCheckCircle size={64} className="text-success mb-3" />
                            <h4 className="text-success">Credit Note Created</h4>
                            <p className="text-muted">Credit note has been created with journal entries.</p>
                        </div>

                        {createdCreditNote && (
                            <div className="border rounded p-4 mb-4 bg-light">
                                <h5 className="mb-3">Credit Note Details</h5>
                                <Row className="text-start">
                                    <Col md={6}>
                                        <div className="mb-2">
                                            <strong>Credit Note No:</strong>
                                            <div className="h5 text-primary">{createdCreditNote.ref_no}</div>
                                        </div>
                                        <div className="mb-2">
                                            <strong>Date:</strong>
                                            <div>{new Date(createdCreditNote.created_at).toLocaleDateString()}</div>
                                        </div>
                                        <div className="mb-2">
                                            <strong>Original Invoice:</strong>
                                            <div>{invoice?.ref_no}</div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="mb-2">
                                            <strong>Total Amount:</strong>
                                            <div className="h4 text-danger">-{createdCreditNote.total_amount} AED</div>
                                        </div>
                                        <div className="mb-2">
                                            <strong>Type:</strong>
                                            <div>
                                                <Badge bg={createdCreditNote.credit_type === 'full' ? 'success' : 'warning'}>
                                                    {createdCreditNote.credit_type.toUpperCase()}
                                                </Badge>
                                            </div>
                                        </div>
                                        {createdCreditNote.journal_id && (
                                            <div className="mb-2">
                                                <strong>Journal ID:</strong>
                                                <div>{createdCreditNote.journal_id}</div>
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                            </div>
                        )}

                        <div className="mt-4 small text-muted">
                            <p>
                                <FaInfoCircle className="me-1" />
                                The credit note has been recorded in the accounting system.
                                {invoice?.pi_status === 'payment_received' &&
                                    ' Customer credit has been created for future invoices.'}
                            </p>
                        </div>
                    </Card.Body>
                </Card>

                {/* Preview Offcanvas */}
                {createdCreditNote && (
                    <CreditNotePreview
                        show={showPreview}
                        handleClose={() => setShowPreview(false)}
                        creditNote={createdCreditNote}
                        onDownloadPDF={handleDownloadPDF}
                    />
                )}
            </>
        );
    };

    // Helper function to convert number to words
    // Helper function to convert number to words
    const numberToWords = (num) => {
        // Fixed: Don't reassign const variables
        const a = [
            '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
            'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen',
            'Eighteen', 'Nineteen'
        ];
        const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        const n = Math.abs(Math.round(num * 100) / 100);
        let whole = Math.floor(n); // Changed to let
        const fraction = Math.round((n - whole) * 100);

        let words = '';

        if (whole === 0) {
            words = 'Zero';
        } else {
            // Handle hundreds
            if (whole >= 100) {
                words += a[Math.floor(whole / 100)] + ' Hundred ';
                whole %= 100;
            }

            // Handle tens
            if (whole >= 20) {
                words += b[Math.floor(whole / 10)] + ' ';
                whole %= 10;
            }

            // Handle units
            if (whole > 0) {
                words += a[whole] + ' ';
            }
        }

        words += 'Dirham';

        if (fraction > 0) {
            words += ' and ' + (fraction < 10 ? '0' + fraction : fraction) + ' Fils';
        }

        words += ' Only';

        return words;
    };

    if (!invoice) return null;

    return (
        <Offcanvas
            show={show}
            onHide={handleCloseOffcanvas}
            placement="end"
            style={{ width: '1000px' }}
            className="credit-note-wizard"
        >
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title>
                    <div className="d-flex align-items-center w-100">
                        <FaFileInvoice className="me-2 text-primary" size={28} />
                        <div className="flex-grow-1">
                            <h5 className="mb-0">Create Credit Note</h5>
                            <small className="text-muted">
                                For Invoice: {invoice.ref_no} |
                                Client: {invoice.quotation?.project?.client?.name ||
                                    invoice.proforma_invoice?.quotation?.project?.client?.name} |
                                Type: {creditNote.invoice_type}
                            </small>
                        </div>
                        <Badge bg="dark" className="fs-6">
                            Step {currentStep} of {steps.length}
                        </Badge>
                    </div>
                </Offcanvas.Title>
            </Offcanvas.Header>

            {/* Progress Bar */}
            <div className="border-bottom p-3 bg-light">
                <div className="d-flex justify-content-between">
                    {steps.map(step => (
                        <div
                            key={step.number}
                            className={`text-center ${step.number <= currentStep ? 'text-primary' : 'text-muted'}`}
                            style={{ flex: 1 }}
                        >
                            <div className={`circle-step ${step.number <= currentStep ? 'active' : ''}`}>
                                {step.number}
                            </div>
                            <div className="small mt-1">{step.title}</div>
                            <div className="x-small text-muted">{step.description}</div>
                        </div>
                    ))}
                </div>
            </div>

            <Offcanvas.Body style={{ overflowY: 'auto', height: 'calc(100vh - 180px)' }}>

                <div className="credit-note-container">
                    {/* Render current step */}
                    {currentStep === 1 && (
                        <Card>
                            <Card.Header className="bg-light">
                                <h6 className="mb-0">Step 1: Basic Details</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Date *</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={creditNote.date}
                                                onChange={(e) => handleInputChange('date', e.target.value)}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Original Invoice</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={invoice?.ref_no || ''}
                                                readOnly
                                                className="bg-light"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Client *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={
                                                    invoice?.quotation?.project?.client?.name ||
                                                    invoice?.proforma_invoice?.quotation?.project?.client?.name ||
                                                    ''
                                                }
                                                readOnly
                                                className="bg-light"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Project</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={
                                                    invoice?.quotation?.project?.name ||
                                                    invoice?.proforma_invoice?.quotation?.project?.name ||
                                                    ''
                                                }
                                                readOnly
                                                className="bg-light"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Attention To</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={creditNote.client_attention}
                                                onChange={(e) => handleInputChange('client_attention', e.target.value)}
                                                placeholder="Client contact person"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Contact Phone</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={creditNote.client_phone}
                                                onChange={(e) => handleInputChange('client_phone', e.target.value)}
                                                placeholder="Client phone number"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>Reason for Credit Note *</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={creditNote.reason || ''}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            setCreditNote(prev => ({
                                                ...prev,
                                                reason: newValue
                                            }));
                                        }}
                                        placeholder="Enter detailed reason for credit note (e.g., Return of goods, Service cancellation, Pricing adjustment)..."
                                        required
                                        style={{ minHeight: '100px' }}
                                    />
                                    <Form.Text className="text-muted">
                                        This will appear on the credit note document
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Additional Remarks</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        value={creditNote.remarks}
                                        onChange={(e) => handleInputChange('remarks', e.target.value)}
                                        placeholder="Any additional notes or instructions..."
                                    />
                                </Form.Group>
                            </Card.Body>
                        </Card>
                    )}
                    {currentStep === 2 && <Step2CreditItems />}
                    {currentStep === 3 && <Step3Preview />}
                    {currentStep === 4 && <Step4Complete />}

                    {/* Navigation Buttons */}
                    {currentStep !== 4 && (
                        <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                            <div>
                                {currentStep > 1 && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={prevStep}
                                        disabled={saving}
                                    >
                                        <FaArrowLeft className="me-2" />
                                        Previous
                                    </Button>
                                )}
                            </div>
                            <div>
                                {currentStep < 3 ? (
                                    <Button
                                        variant="primary"
                                        onClick={nextStep}
                                        disabled={
                                            (currentStep === 1 && !creditNote.reason.trim()) ||
                                            (currentStep === 2 && (!dataLoaded || creditNote.total_amount <= 0))
                                        }
                                    >
                                        Next
                                        <FaArrowRight className="ms-2" />
                                    </Button>
                                ) : (
                                    <Button
                                        variant="success"
                                        onClick={handleSubmit}
                                        disabled={saving || creditNote.total_amount <= 0 || !creditNote.reason.trim()}
                                    >
                                        {saving ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Creating...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="me-2" />
                                                Create Credit Note
                                            </>
                                        )}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* For Step 4, show close button */}
                    {currentStep === 4 && (
                        <div className="text-center mt-4">
                            <Button
                                variant="outline-primary"
                                onClick={handleCloseOffcanvas}
                            >
                                Close
                            </Button>
                        </div>
                    )}
                </div>

            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default CreditNoteCreateOffcanvas;